import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Grid, Paper, Table, TableBody, TableCell
    , TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Print as IconPrint, PictureAsPdf as IconPdf, Description as IconWord
    , TableView as IconExcel, ArrowBack as IconVolver } from "@mui/icons-material";

import GraficoBarras from "./GraficoBarras";
import { exportarExcel, exportarPdf, exportarWord, imprimirReporte } from "../../services/reporteExportService";
import { registrarEjecucionReporte } from "../../services/bitacoraReporteService";

// Pagina de reporte: filtros, indicadores, grafica y tabla, con las cuatro
// salidas (imprimir, PDF, Word y Excel).
//
// La tabla es HTML plano y no el DataGrid de las ventanas de mantenimiento:
// el DataGrid virtualiza filas y arrastra estilos que no se imprimen bien, y el
// reporte tiene que salir tal cual en papel.
const ReportePagina = ({
    titulo,
    subtitulo,
    filtros,
    indicadores,
    cabecera,
    columnas,
    filas,
    grafico,
    cargando,
}) => {
    const location = useLocation();
    const datos = { titulo, subtitulo, indicadores, columnas, filas };
    const ultimaCargaRef = useRef(null);
    const tiempoInicioCargaRef = useRef(performance.now());

    useEffect(() => {
        if (cargando) {
            tiempoInicioCargaRef.current = performance.now();
        } else if (filas && Array.isArray(filas)) {
            const claveCarga = `${location.pathname}_${filas.length}_${titulo}`;
            if (ultimaCargaRef.current !== claveCarga) {
                ultimaCargaRef.current = claveCarga;
                const duracionRetrieval = Math.max(0, Math.round(performance.now() - tiempoInicioCargaRef.current));
                registrarEjecucionReporte({
                    reportPath: location.pathname || `/Reporte/${titulo}`,
                    format: "HTML",
                    reportAction: "Render",
                    timeDataRetrieval: duracionRetrieval,
                    timeProcessing: 15,
                    timeRendering: 25,
                    rowCount: filas.length,
                    status: "rsSuccess"
                });
            }
        }
    }, [cargando, filas, location.pathname, titulo]);

    const handleExportar = (formato, fnExport) => {
        const timeStart = new Date();
        const t0 = performance.now();
        try {
            fnExport();
        } finally {
            const timeEnd = new Date();
            const duracion = Math.max(0, Math.round(performance.now() - t0));
            registrarEjecucionReporte({
                reportPath: location.pathname || `/Reporte/${titulo}`,
                format: formato,
                reportAction: "Render",
                timeStart,
                timeEnd,
                timeRendering: duracion,
                rowCount: filas?.length || 0,
                status: "rsSuccess"
            });
        }
    };

    return (
        <Box className="area-reporte">
            {/* Solo aparece al imprimir: en pantalla el logo y el nombre ya estan
                en la barra superior de la aplicacion. */}
            <Box className="encabezado-impresion">
                <Box className="marca">KOLOB &middot; Sistema Terap&eacute;tico</Box>
                <Box className="meta">
                    {titulo} &nbsp;|&nbsp; Generado el {new Date().toLocaleString("es-PE")}
                    {subtitulo ? " | " + subtitulo : ""}
                </Box>
            </Box>
            <Grid container>
                {/* Volver a la portada de reportes, de donde se abrio este.
                    No se imprime: en papel no tiene sentido. */}
                <Grid item xs={12} className="no-imprimir">
                    <Button
                        component={Link}
                        to="/reporte"
                        startIcon={<IconVolver />}
                        size="small"
                        sx={{ marginBottom: 1, paddingLeft: 0 }}
                    >
                        Volver a Reportes
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {titulo}
                    </Typography>
                </Grid>
                {subtitulo ? (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            {subtitulo}
                        </Typography>
                    </Grid>
                ) : null}

                {filtros ? (
                    <Grid item xs={12}>
                        <Box sx={{ paddingTop: "12px" }}>{filtros}</Box>
                    </Grid>
                ) : null}

                {cargando ? (
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", padding: "32px" }}>
                        <CircularProgress size={28} />
                    </Grid>
                ) : (
                    <>
                        <Grid item xs={12} className="no-imprimir">
                            <Grid container spacing={1} sx={{ paddingTop: "12px" }}>
                                {indicadores.map((indicador) => (
                                    <Grid item xs={6} sm={3} key={indicador.etiqueta}>
                                        <Paper variant="outlined" sx={{ padding: "10px 12px" }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {indicador.etiqueta}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: "bold", color: indicador.color }}>
                                                {indicador.valor}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        <Grid item xs={12} sx={{ paddingTop: "12px" }} className="no-imprimir">
                            <Grid container spacing={1} justifyContent="flex-end">
                                <Grid item>
                                    <Button variant="outlined" startIcon={<IconPrint />} onClick={() => handleExportar("PRINT", () => imprimirReporte(titulo))}>
                                        Imprimir
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="outlined" startIcon={<IconPdf />} onClick={() => handleExportar("PDF", () => exportarPdf(titulo))}>
                                        PDF
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="outlined" startIcon={<IconWord />} onClick={() => handleExportar("WORD", () => exportarWord(datos))}>
                                        Word
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="outlined" startIcon={<IconExcel />} onClick={() => handleExportar("EXCEL", () => exportarExcel(datos))}>
                                        Excel
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>

                        {grafico ? (
                            <Grid item xs={12} sx={{ paddingTop: "12px" }}>
                                <GraficoBarras
                                    titulo={grafico.titulo}
                                    subtitulo={grafico.subtitulo}
                                    etiquetas={grafico.etiquetas}
                                    valores={grafico.valores}
                                />
                            </Grid>
                        ) : null}

                        {/* Datos principales de lo que se reporta, sobre la
                            tabla: los de la terapia y del plan. */}
                        {cabecera && cabecera.length ? (
                            <Grid item xs={12} sx={{ paddingTop: "12px" }}>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableBody>
                                            {cabecera.map((dato) => (
                                                <TableRow key={dato.etiqueta}>
                                                    <TableCell sx={{ fontWeight: "bold", width: 180, fontSize: 13 }}>
                                                        {dato.etiqueta}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 13 }}>{dato.valor}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        ) : null}

                        <Grid item xs={12} sx={{ paddingTop: "12px" }}>
                            <TableContainer>
                                <Table size="small" sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "#5b2d8e" }}>
                                            {columnas.map((columna) => (
                                                <TableCell key={columna.campo} align={columna.alin || "left"}
                                                    sx={{ color: "#fff", fontWeight: "bold" }}>
                                                    {columna.titulo}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filas.map((fila, indice) => (
                                            <TableRow key={indice} hover={indice % 2 === 1}
                                                sx={{ backgroundColor: indice % 2 === 1 ? "#f6f2fb" : "inherit" }}>
                                                {columnas.map((columna) => (
                                                    <TableCell key={columna.campo} align={columna.alin || "left"} sx={{ fontSize: 13 }}>
                                                        {columna.render ? columna.render(fila) : fila[columna.campo]}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                        {!filas.length ? (
                                            <TableRow>
                                                <TableCell colSpan={columnas.length} align="center" sx={{ padding: "24px" }}>
                                                    No hay datos para los filtros indicados.
                                                </TableCell>
                                            </TableRow>
                                        ) : null}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
}

export default ReportePagina;